import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User as UserEntity } from '@/api/entities';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Search, Shield, Loader2, AlertCircle, Sparkles, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { createPageUrl } from '@/utils';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';
import VipBadge from '@/components/admin/VipBadge';

const ITEMS_PER_PAGE = 50;

export default function UsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [milesBackfillStatus, setMilesBackfillStatus] = useState(null);
  const [milesRemoveStatus, setMilesRemoveStatus] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const me = await UserEntity.me();
        setCurrentUser(me);
        if (me.role !== 'admin') {
          setUsers([]);
          throw new Error("Access Denied");
        }
        const allUsers = await UserEntity.list();
        setUsers(allUsers);
      } catch (error) {
        console.error("Failed to load user data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter]);

  const runMilesBackfill = async () => {
    setMilesBackfillStatus('running');
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const res = await fetch('/api/admin/miles/backfill', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const data = await res.json();
      setMilesBackfillStatus(res.ok ? { success: true, ...data } : { error: data.error || 'Failed' });
    } catch (e) {
      setMilesBackfillStatus({ error: e.message });
    }
  };

  const runMilesRemoveNonCompleted = async () => {
    if (!confirm('Remove miles awarded for trips that are not completed? This cannot be undone.')) return;
    setMilesRemoveStatus('running');
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');
      const res = await fetch('/api/admin/miles/remove-non-completed', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const data = await res.json();
      setMilesRemoveStatus(res.ok ? { success: true, ...data } : { error: data.error || 'Failed' });
    } catch (e) {
      setMilesRemoveStatus({ error: e.message });
    }
  };

  const handleRoleChange = async (userId, newCustomRole) => {
    try {
      const newSystemRole = newCustomRole === 'Admin' ? 'admin' : 'user';
      await UserEntity.update(userId, { custom_role: newCustomRole, role: newSystemRole });
      setUsers(users.map((u) => u.id === userId ? { ...u, custom_role: newCustomRole, role: newSystemRole } : u));
    } catch (error) {
      console.error(`Failed to update role for user ${userId}:`, error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-center p-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-3xl">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-light text-white mb-2 tracking-tight">Access Denied</h2>
          <p className="text-white/60 text-sm font-normal">Administrator access required.</p>
        </motion.div>
      </div>
    );
  }

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) {
      const displayRole = user.custom_role || (user.role === 'admin' ? 'Admin' : 'User');
      const matchesRole = roleFilter === 'all' || displayRole.toLowerCase() === roleFilter.toLowerCase();
      return matchesRole;
    }
    
    // Search across multiple fields
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
    const email = (user.email || '').toLowerCase();
    const userId = (user.id || '').toLowerCase();
    const phone = (user.phone || user.phone_number || user.phoneNumber || '').replace(/[\s\-\(\)]/g, '').toLowerCase();
    const searchClean = searchLower.replace(/[\s\-\(\)]/g, '');
    
    const matchesSearch = 
      fullName.includes(searchLower) ||
      (user.firstName || '').toLowerCase().includes(searchLower) ||
      (user.lastName || '').toLowerCase().includes(searchLower) ||
      email.includes(searchLower) ||
      userId.includes(searchLower) ||
      (phone && phone.includes(searchClean));
    
    const displayRole = user.custom_role || (user.role === 'admin' ? 'Admin' : 'User');
    const matchesRole = roleFilter === 'all' || displayRole.toLowerCase() === roleFilter.toLowerCase();
    
    return matchesSearch && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  return (
    <div className="min-h-[70vh] py-8 relative">
      <RoyalAmbientBackground />
      <div className="max-w-5xl mx-auto px-6 md:px-8 relative z-10">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-white/40" />
            <span className="text-white/40 text-xs font-normal tracking-widest uppercase">{users.length} Active Users</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-3 tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 md:w-10 md:h-10" />
            Users
          </h1>
          <p className="text-white/60 text-sm md:text-base lg:text-lg font-light">Manage user roles and permissions</p>
        </motion.div>

        {/* Miles tools - Admin */}
        {(milesBackfillStatus || milesRemoveStatus) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4 p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
            {milesBackfillStatus && (
              <div>
                {milesBackfillStatus === 'running' ? (
                  <span className="text-white/80 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Running backfill...</span>
                ) : milesBackfillStatus.error ? (
                  <span className="text-red-400">Backfill error: {milesBackfillStatus.error}</span>
                ) : (
                  <span className="text-green-400">{milesBackfillStatus.message}</span>
                )}
              </div>
            )}
            {milesRemoveStatus && (
              <div>
                {milesRemoveStatus === 'running' ? (
                  <span className="text-white/80 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Removing non-completed miles...</span>
                ) : milesRemoveStatus.error ? (
                  <span className="text-red-400">Remove error: {milesRemoveStatus.error}</span>
                ) : (
                  <span className="text-green-400">{milesRemoveStatus.message}</span>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Search and Filter */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search by name, email, phone, or user ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-white/5 text-white border-white/10 pl-11 focus:border-white/20 h-12 rounded-2xl font-normal placeholder:text-white/40"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full md:w-40 bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-12 rounded-2xl font-normal">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-white/60" />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent className="font-normal">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="investor">Investor</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto h-12 px-4 bg-brand-500/10 border-brand-500/20 text-brand-200 hover:bg-brand-500/20 flex items-center gap-2"
              onClick={runMilesBackfill}
              disabled={milesBackfillStatus === 'running'}
            >
              {milesBackfillStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Miles Backfill
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto h-12 px-4 bg-red-500/10 border-red-500/20 text-red-200 hover:bg-red-500/20 flex items-center gap-2"
              onClick={runMilesRemoveNonCompleted}
              disabled={milesRemoveStatus === 'running'}
            >
              {milesRemoveStatus === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remove Non-Completed Miles
            </Button>
          </div>
        </motion.div>

        {/* Users List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden"
        >
          <div className="divide-y divide-white/10">
            {paginatedUsers.map((user, index) => {
              const displayRole = user.custom_role || (user.role === 'admin' ? 'Admin' : 'User');

              return (
                <motion.div
                  key={user.id}
                  className="p-5 flex items-center justify-between gap-4 hover:bg-white/5 transition-all duration-300 cursor-pointer group"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02, duration: 0.2 }}
                  onClick={() => navigate(createPageUrl(`UserDetails?id=${user.id}`))}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="min-w-0 flex-1">
                      {/* Mobile: Stack name and email */}
                      <div className="md:hidden">
                        <p className="text-sm font-normal text-white truncate flex items-center gap-1.5">
                          <span className="truncate">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}</span>
                          <VipBadge userId={user.id} />
                        </p>
                        <p className="text-xs text-white/60 truncate mt-1 font-normal">
                          {user.email}
                        </p>
                      </div>
                      {/* Desktop: Name and email on one line */}
                      <div className="hidden md:block">
                        <p className="text-base font-normal text-white truncate">
                          {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
                          <VipBadge userId={user.id} className="ml-2 align-middle" />
                          <span className="text-sm text-white/60 font-normal ml-3">
                            {user.firstName && user.lastName ? user.email : ''}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={displayRole}
                      onValueChange={(newRole) => handleRoleChange(user.id, newRole)}
                      disabled={user.id === currentUser.id}
                    >
                      <SelectTrigger className="w-28 bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 text-sm h-10 px-4 font-normal">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="text-sm font-normal">
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="User">User</SelectItem>
                        <SelectItem value="Investor">Investor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Pagination Controls */}
        {filteredUsers.length > ITEMS_PER_PAGE && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-6 mb-8"
          >
            {/* Desktop pagination */}
            <div className="hidden md:flex items-center justify-between">
              <div className="text-sm text-white/60 font-normal">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed h-10 px-4 font-normal"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm text-white/60 font-normal px-4">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed h-10 px-4 font-normal"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
            
            {/* Mobile pagination - simplified */}
            <div className="flex md:hidden items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed h-10 w-10 p-0 font-normal"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="text-sm text-white/60 font-normal">
                Page {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="bg-white/5 text-white border-white/10 hover:bg-white/10 hover:border-white/20 disabled:opacity-50 disabled:cursor-not-allowed h-10 w-10 p-0 font-normal"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
